import { useEffect, useState } from "react";
import { InventoryStatusResponseSchema, Permission } from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useAuthStore, useHasPermission } from "@/store/authStore";

/** Shows inventory for existing items when the organization has imported stock. */
export function useHasInventory(enabled: boolean): boolean {
  const canView = useHasPermission(Permission.ITEMS_VIEW);
  const canViewLocations = useHasPermission(Permission.LOCATIONS_VIEW);
  const organizationId = useAuthStore((state) => state.user?.organization.id);
  const [status, setStatus] = useState<{
    organizationId: string;
    hasInventory: boolean;
  } | null>(null);

  useEffect(() => {
    if (!enabled || !canView || !canViewLocations || !organizationId) {
      return;
    }
    let cancelled = false;
    const loadStatus = async () => {
      try {
        const response = await fetchApi("inventory-lines/status", "GET");
        if (!response.ok) {
          throw new Error(response.error ?? "Unable to load inventory status");
        }
        const data = InventoryStatusResponseSchema.parse(response.data);
        if (!cancelled) {
          setStatus({ organizationId, ...data });
        }
      } catch (error) {
        captureException(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [enabled, canView, canViewLocations, organizationId]);

  return Boolean(
    enabled &&
      canView &&
      canViewLocations &&
      status?.organizationId === organizationId &&
      status?.hasInventory,
  );
}
